import React, { useContext, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useDispatch } from "react-redux";

import { Grid, FormHelperText } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import SaveIcon from "@mui/icons-material/Save";

import MDTypography from "components/MDTypography";

import MDBox from "components/MDBox";

import MDButton from "components/MDButton";

import { UserContext } from "App";

import FullScreenLoader from "components/Custom/FullScreenLoader";

import { popUp, saveMsg, deleteMsg } from "components/Custom/AreYouSurePopUp";

import {
  UploadEventMediaAPI,
  EditEventAPI,
  GetEventDetailsAPI,
  DeleteEventMediaAPI,
} from "APIs/EventAPIs";

import EventSchedules from "./schedules";

import { EventDetailsFields, MediaPicker, MediaGrid } from "./fromHeader";

import { useEventForm } from "./useEventForm";

import { buildEventPayload, buildMediaFormData } from "./eventFromUtils";

import { notify } from "App";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "edit-event-form";

const IMAGE_REQUIRED_MSG = "At least one image is required.";

const LAST_IMAGE_MSG =
  "An event needs at least one image. Add a new image before removing this one.";

const EditEvent = () => {
  const userData = useContext(UserContext);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { id } = useParams();

  const { accentName } = useAccent();

  const {
    eventData,
    setEventData,
    scheduleData,
    loadSchedules,
    handleEventChange,
    handleEventBlur,
    handleScheduleChange,
    handleScheduleBlur,
    addSchedule,
    deleteSchedule,
    reorderSchedule,
    getVisibleEventError,
    getVisibleScheduleError,
    isFormValid,
    touchAllFields,
  } = useEventForm();

  const [existingMedia, setExistingMedia] = useState([]);

  const [loading, setLoading] = useState(false);

  // Loader shown in place of the upload input while images are uploading
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Image is compulsory: error is shown in red under the upload input
  const [imageError, setImageError] = useState("");

  const viewPath = `/event/view/${id}`;
  const [isFetchingData, setIsFetchingData] = useState(true);

  useEffect(() => {
    if (!userData?.access_token) return;

    const loadEvent = async () => {
      setIsFetchingData(true);
      const response = await GetEventDetailsAPI(id, userData.access_token, dispatch);

      if (response === null) return;

      setEventData({
        event_id: response.events_id,
        event_title: response.event_title || "",
        event_description: response.event_description
          ? response.event_description.replace(/\\n/g, "\n")
          : "",
        event_start_date: response.event_start_date?.slice(0, 10) || "",
        event_end_date: response.event_end_date?.slice(0, 10) || "",
      });

      loadSchedules(response.schedule_list || []);
      setExistingMedia(response.media_list || []);
      setIsFetchingData(false);
    };

    loadEvent();

    // setEventData / loadSchedules are stable enough for a one-off load per event
  }, [userData, id, dispatch]);

  // Upload right after files are selected, then refresh the media list
  const handleFileChange = async (e) => {
    const files = Array.from(e?.target?.files || e || []);

    if (files.length === 0) return;

    setIsImageLoading(true);

    try {
      const eventId = eventData.event_id || id;

      await UploadEventMediaAPI(
        buildMediaFormData(
          eventId,
          files.map((file, index) => ({ id: `${Date.now()}-${index}`, file }))
        ),
        userData.access_token,
        dispatch
      );

      const response = await GetEventDetailsAPI(id, userData.access_token, dispatch);

      if (response !== null) {
        setExistingMedia(response.media_list || []);
      }

      notify("Images uploaded successfully!", "success");

      setImageError("");

      if (e?.target) e.target.value = "";
    } catch (error) {
      console.error(error);
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleDeleteExistingMedia = (eventMediaId) => {
    // The last image can't be removed: an event needs at least one image
    if (existingMedia.length === 1) {
      setImageError(LAST_IMAGE_MSG);
      return;
    }

    popUp(
      async () => {
        await DeleteEventMediaAPI(eventMediaId, userData.access_token, dispatch);

        setExistingMedia((prev) => prev.filter((item) => item.event_media_id !== eventMediaId));
      },
      undefined,
      deleteMsg("image")
    );
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const payload = buildEventPayload(eventData, scheduleData);

      const response = await EditEventAPI(payload, userData.access_token, dispatch);

      if (response === null) return;

      notify("Event edited!", "success");

      navigate(viewPath);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const hasNoImage = existingMedia.length === 0;

    // Invalid form or no image: show every error (missing fields + image) at once
    if (!isFormValid || hasNoImage) {
      if (hasNoImage) setImageError(IMAGE_REQUIRED_MSG);
      if (!isFormValid) touchAllFields();
      return;
    }

    await popUp(handleSave, undefined, saveMsg("event"));
  };

  // Back always works, even when fields are empty or wrong (validation only applies to Save)
  const handleBack = () => {
    navigate(-1);
  };

  const existingImageItems = existingMedia.map((item) => ({
    key: item.event_media_id,
    src: item.thumb || item.media,
    href: item.media,
    alt: `media_${item.event_media_id}`,
    onDelete: () => handleDeleteExistingMedia(item.event_media_id),
  }));

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      <SectionHeader
        icon="edit"
        label="EDIT EVENT"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </MDButton>
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          <form id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
            <SectionCard title="Details" subtitle="Fields marked * are required">
              <EventDetailsFields
                eventData={eventData}
                onChange={handleEventChange}
                onBlur={handleEventBlur}
                getVisibleError={getVisibleEventError}
              />
            </SectionCard>

            <EventSchedules
              data={scheduleData}
              onAdd={addSchedule}
              onChange={handleScheduleChange}
              onBlur={handleScheduleBlur}
              onDelete={deleteSchedule}
              onReorder={reorderSchedule}
              getVisibleError={getVisibleScheduleError}
              eventStartDate={eventData.event_start_date}
              eventEndDate={eventData.event_end_date}
            />

            <SectionCard title="Media" subtitle="At least one image is required">
              <Grid container spacing={2}>
                {/* Existing Images Section */}
                <Grid item xs={12}>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color={accentName}
                    display="block"
                    mb={1.5}
                  >
                    Existing Media ({existingImageItems.length})
                  </MDTypography>

                  {existingImageItems.length > 0 ? (
                    <MediaGrid items={existingImageItems} />
                  ) : (
                    <MDTypography variant="button" color="text">
                      No existing media attached.
                    </MDTypography>
                  )}
                </Grid>

                {/* Upload Input */}
                <FormField name="upload_additional_media" required full>
                  {isImageLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <MediaPicker onChange={handleFileChange} />
                      {imageError && <FormHelperText error>{imageError}</FormHelperText>}
                    </>
                  )}
                </FormField>
              </Grid>
            </SectionCard>
          </form>

          {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save still submits the form through the `form` attribute. */}
          <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
            <MDButton
              type="button"
              variant="outlined"
              color="primary"
              sx={ranuOutlinedButtonSx}
              disabled={loading}
              onClick={() => navigate(-1)}
            >
              Cancel
            </MDButton>
            <MDButton
              color="primary"
              type="submit"
              form={FORM_ID}
              sx={ranuButtonSx}
              disabled={loading}
            >
              Save Event
            </MDButton>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default EditEvent;
